import React, { useState, useEffect } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { useAuth } from "../../../context/AuthContext";
import PageTitle from "../../layouts/PageTitle";
import { db } from "../../../firebase";
import { getDocs, getDoc, collection, doc } from "firebase/firestore";
import "./NodeList.css";
import NodeRegister from "./NodeRegister";
import EditCustomerModal from "./EditCustomerModal";
import EditNodeModal from "./EditNodeModal";
import CustomerBookingQrModal from "./CustomerBookingQrModal";
import CustomerLocation from "./CustomerLocation";
import CustomerSleepServices from "./CustomerSleepServices";
import { filterNodeDirectory, getNodeSharedRoomCount } from "./nodeDirectoryUtils";
import { canShareCustomerBooking } from "./customerBookingQr";
import { getAvailabilityStatus, getAvailabilityLabel, isNodeBookingAvailable } from "../../../utils/nodeAvailability";
import {
  filterCustomersForStaffNode,
  isStaffCustomerScopedUser,
  normalizeAccessList,
} from "../../../utils/staffCustomerAccess";

const NodeList = () => {
  const [nodes, setNodes] = useState([]);
  const [bookingQr, setBookingQr] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [availability, setAvailability] = useState("");
  const [service, setService] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const { currentUser } = useAuth();

  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [showEditNodeModal, setShowEditNodeModal] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [nodeEdit, setNodeEdit] = useState({});
  const [isCreateCustomerMode, setIsCreateCustomerMode] = useState(false);

  const addNode = (node) => {
    setNodes((prevNodes) => [node, ...prevNodes]);
  };

  const getAllNodes = async (adminData) => {
    try {
      const nodesSnapshot = await getDocs(collection(db, "nodes"));
      const allowedNodeCodes = normalizeAccessList(adminData && adminData.node);
      const visibleNodeDocs = isStaffCustomerScopedUser(currentUser)
        ? nodesSnapshot.docs.filter((nodeDoc) =>
            allowedNodeCodes.includes(nodeDoc.data().nodecode),
          )
        : nodesSnapshot.docs;

      const nodePromises = visibleNodeDocs.map(async (nodeDoc) => {
        const nodeData = nodeDoc.data();

        const customersSnapshot = await getDocs(
          collection(db, "nodes", nodeDoc.id, "customers"),
        );

        const customersData = filterCustomersForStaffNode({
          customers: customersSnapshot.docs,
          currentUser,
          adminData,
          nodeCode: nodeData.nodecode,
          getCustomerId: (customerDoc) => customerDoc.id,
        }).map((customerDoc) => ({
          ...customerDoc.data(),
          emailList: customerDoc.data().emailList || [],
          id: customerDoc.id,
        }));

        return {
          ...nodeData,
          customers: customersData,
          id: nodeDoc.id,
        };
      });

      return await Promise.all(nodePromises);
    } catch (error) {
      console.error("Error fetching nodes:", error);
      throw error;
    }
  };

  const handleOnNewNode = () => {
    setShowRegisterModal(true);
  };

  const handleOnRegisterHide = () => {
    setShowRegisterModal(false);
  };

  const handleAddCustomer = (nodeId) => {
    setSelectedCustomer(null);
    setSelectedNodeId(nodeId);
    setIsCreateCustomerMode(true);
    setShowEditCustomerModal(true);
  };

  const handleEditCustomer = (customer, nodeId) => {
    setSelectedCustomer(customer);
    setSelectedNodeId(nodeId);
    setIsCreateCustomerMode(false);
    setShowEditCustomerModal(true);
  };

  const handleCustomerUpdate = (customerId, updatedData) => {
    setNodes((prevNodes) =>
      prevNodes.map((node) => {
        if (node.id !== selectedNodeId) return node;

        return {
          ...node,
          customers: (node.customers || []).map((customer) =>
            customer.id === customerId
              ? { ...customer, ...updatedData }
              : customer,
          ),
        };
      }),
    );
  };

  const handleCustomerCreate = (newCustomer) => {
    setNodes((prevNodes) =>
      prevNodes.map((node) => {
        if (node.id !== selectedNodeId) return node;

        return {
          ...node,
          customers: [...(node.customers || []), newCustomer],
        };
      }),
    );
  };

  const handleEditNode = (nodeId) => {
    const node = nodes.find((item) => item.id === nodeId);
    setNodeEdit(node || {});
    setSelectedNodeId(nodeId);
    setShowEditNodeModal(true);
  };

  const handleNodeUpdate = (nodeId, updatedData) => {
    setNodes((prevNodes) =>
      prevNodes.map((node) =>
        node.id === nodeId ? { ...node, ...updatedData } : node,
      ),
    );
  };

  useEffect(() => {
    let cancelled = false;
    const fetchNodes = async () => {
      setIsLoading(true);
      setLoadError(false);
      try {
        const currentAdmin = await getDoc(
          doc(db, "admins", currentUser.displayName),
        );

        const currentAdminData = currentAdmin.exists()
          ? currentAdmin.data()
          : {};
        const allNodes = await getAllNodes(currentAdminData);

        if (cancelled) return;

        if (currentAdminData.role === "super_admin") {
          setNodes(allNodes);
        } else {
          const currentNodeCode = currentAdminData.node;

          let nodeCode = [];
          if (Array.isArray(currentNodeCode)) {
            nodeCode = currentNodeCode;
          } else if (currentNodeCode) {
            nodeCode = [currentNodeCode];
          }

          const filteredNodes = allNodes
            .filter((item) => nodeCode.includes(item.nodecode))
            .map((node) => ({
              ...node,
              customers: filterCustomersForStaffNode({
                customers: node.customers,
                currentUser,
                adminData: currentAdminData,
                nodeCode: node.nodecode,
                getCustomerId: (customer) => customer.id,
              }),
            }));

          setNodes(filteredNodes);
        }
      } catch (error) {
        console.error("Error fetching nodes:", error);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (currentUser && currentUser.displayName) {
      fetchNodes();
    }
    return () => { cancelled = true; };
  }, [currentUser, reloadKey]);

  const canManage = Boolean(currentUser && currentUser.roles && currentUser.roles.includes("super_admin"));
  const searchTerm = search.trim().toLocaleLowerCase();
  const visibleNodes = filterNodeDirectory(nodes, { search, availability: canManage ? availability : "", service });
  const customerCount = nodes.reduce((total, node) => total + (node.customers || []).length, 0);
  const shownCustomerCount = visibleNodes.reduce((total, node) => total + node.visibleCustomers.length, 0);
  const hasFilters = Boolean(search || service || (canManage && availability));
  const resetFilters = () => { setSearch(""); setAvailability(""); setService(""); };

  return (
    <React.Fragment>
      <PageTitle activeMenu="Our Nodes" motherMenu="Node" />

      <div className="node-directory">
        <div className="node-directory__toolbar">
          <div className="node-directory__heading">
            <h2>Our Nodes</h2>
            {!isLoading && !loadError && (
              <p>{nodes.length} nodes <span aria-hidden="true">·</span> {customerCount} customers</p>
            )}
          </div>
          <div className="node-directory__controls">
            <Button variant="light" disabled={isLoading} onClick={() => setReloadKey(value => value + 1)}><i className="fa fa-refresh mr-1" aria-hidden="true" />Refresh</Button>
            {canManage && <Button onClick={handleOnNewNode} variant="primary">+ New Node</Button>}
          </div>
        </div>
        <div className="node-directory__filters">
            <div className="node-directory__search">
              <i className="fa fa-search" aria-hidden="true" />
              <Form.Control
                type="search"
                aria-label="Search nodes or customers"
                placeholder="Search node, code or customer"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {canManage && <Form.Control as="select" aria-label="Node availability" value={availability} onChange={event => setAvailability(event.target.value)}><option value="">All node statuses</option><option value="available">Available nodes</option><option value="unavailable">Unavailable nodes</option></Form.Control>}
            <Form.Control as="select" aria-label="Customer service" value={service} onChange={event => setService(event.target.value)}><option value="">All customer services</option><option value="hospital">Sleep in Hospital</option><option value="home">Home Sleep</option><option value="opd">OPD Doctor Visit</option></Form.Control>
            {hasFilters && <Button variant="light" onClick={resetFilters}>Clear</Button>}
        </div>

        {isLoading ? (
          <div className="node-directory__state" role="status">
            <Spinner animation="border" size="sm" aria-hidden="true" />
            <span>Loading nodes…</span>
          </div>
        ) : loadError ? (
          <div className="node-directory__state" role="alert">
            <div>
              <h3>Unable to load nodes</h3>
              <p>Please try again.</p>
              <Button variant="outline-primary" onClick={() => setReloadKey((value) => value + 1)}>Retry</Button>
            </div>
          </div>
        ) : (
          <React.Fragment>
            <div className="node-directory__results" role="status">
              Showing {visibleNodes.length} of {nodes.length} nodes
              {` · ${shownCustomerCount} customers`}
              {searchTerm && " · Matching node or customer names"}
            </div>
            {visibleNodes.length === 0 ? (
              <div className="node-directory__state">
                <div>
                  <h3>{hasFilters ? "No matching nodes" : "No nodes to display"}</h3>
                  <p>{hasFilters ? "Try a different search, status or customer service." : "No nodes are available for your account."}</p>
                  {hasFilters && <Button variant="outline-primary" onClick={resetFilters}>Clear filters</Button>}
                </div>
              </div>
            ) : (
              <div className="node-directory__grid">
                {visibleNodes.map((node) => (
                  <article className={`node-card${canManage && getAvailabilityStatus(node) === "unavailable" ? " node-card--unavailable" : ""}`} key={node.id} aria-label={node.nodename || "Node"}>
                    <div className="node-card__header">
                      <div className="node-card__identity">
                        <div className="node-card__badges">
                          <span className="node-card__code">{node.nodecode || "-"}</span>
                          {canManage && <AvailabilityBadge record={node} />}
                        </div>
                        <h3 title={node.nodename || "-"}>{node.nodename || "-"}</h3>
                      </div>
                      {canManage && (
                        <Button size="sm" variant="outline-primary" aria-label={`Edit node ${node.nodename || ""}`} onClick={() => handleEditNode(node.id)}>Edit</Button>
                      )}
                    </div>
                    <dl className="node-card__details">
                      <div><dt>Node Index</dt><dd>{node.nodeIndex == null || node.nodeIndex === "" ? "-" : node.nodeIndex}</dd></div>
                      <div><dt>Due Days</dt><dd>{node.dueDays == null || node.dueDays === "" ? "-" : node.dueDays}</dd></div>
                      <div><dt>Customers</dt><dd>{(node.customers || []).length}</dd></div>
                      <div className="node-card__shared-rooms"><dt>Shared Rooms</dt><dd>{getNodeSharedRoomCount(node) === null ? "Not set" : `${getNodeSharedRoomCount(node)} shared rooms`}</dd></div>
                    </dl>
                    <NodeCustomerInfo
                      value={node.visibleCustomers}
                      totalCount={(node.customers || []).length}
                      nodeId={node.id}
                      node={node}
                      onBookingQr={(customer, nodeId, service = "sleep-test") => setBookingQr({ customer, nodeId, service })}
                      onEditCustomer={handleEditCustomer}
                      onAddCustomer={handleAddCustomer}
                      currentUser={currentUser}
                      canManage={canManage}
                    />
                  </article>
                ))}
              </div>
            )}
          </React.Fragment>
        )}
      </div>

      {bookingQr && canShareCustomerBooking(currentUser) && <CustomerBookingQrModal key={`${bookingQr.nodeId}/${bookingQr.customer.id}/${bookingQr.service}`} customer={bookingQr.customer} nodeId={bookingQr.nodeId} service={bookingQr.service} onHide={() => setBookingQr(null)} />}

      {canManage && <NodeRegister
        item={addNode}
        show={showRegisterModal}
        hide={handleOnRegisterHide}
      />}

      {canManage && showEditCustomerModal && (
        <EditCustomerModal
          show={showEditCustomerModal}
          onHide={() => {
            setShowEditCustomerModal(false);
            setSelectedCustomer(null);
            setSelectedNodeId(null);
            setIsCreateCustomerMode(false);
          }}
          customer={selectedCustomer}
          nodeId={selectedNodeId}
          node={nodes.find((node) => node.id === selectedNodeId)}
          onUpdate={handleCustomerUpdate}
          onCreate={handleCustomerCreate}
          isCreateMode={isCreateCustomerMode}
        />
      )}

      {canManage && showEditNodeModal && (
        <EditNodeModal
          show={showEditNodeModal}
          onHide={() => setShowEditNodeModal(false)}
          node={nodeEdit}
          nodeId={selectedNodeId}
          onUpdate={handleNodeUpdate}
        />
      )}
    </React.Fragment>
  );
};

const AvailabilityBadge = ({ record, parentNode }) => (
  <span className={`node-availability node-availability--${getAvailabilityStatus(record, parentNode)}`}>
    {getAvailabilityLabel(record, parentNode)}
  </span>
);

const NodeCustomerInfo = ({
  value = [],
  totalCount = value.length,
  nodeId,
  node,
  onEditCustomer,
  onBookingQr,
  onAddCustomer,
  currentUser,
  canManage,
}) => (
  <div className="node-card__customers">
    <div className="node-card__customer-heading">
      <h4>Customers <span className="node-card__count">{value.length === totalCount ? value.length : `${value.length} / ${totalCount}`}</span></h4>
      {canManage && (
        <Button size="sm" variant="link" onClick={() => onAddCustomer(nodeId)}>+ New Customer</Button>
      )}
    </div>
    {value.length > 0 ? (
      <ul className="node-card__customer-list" tabIndex={0} aria-label="Refer customers">
        {value.map((customer, index) => (
          <li key={customer.id || index}>
            <div className="node-card__customer-name">
              <strong>{customer.CustomerNameTH || customer.CustomerNameEN || "-"}</strong>
              {customer.CustomerNameEN && customer.CustomerNameEN !== customer.CustomerNameTH && (
                <span>{customer.CustomerNameEN}</span>
              )}
              <div className="node-card__badges">
                {customer.Type && <span className="node-card__customer-type">{customer.Type}</span>}
                {canManage && <AvailabilityBadge record={customer} parentNode={node} />}
              </div>
              <CustomerSleepServices customer={customer} />
              <CustomerLocation customer={customer} />
            </div>
            <div className="node-card__actions">
              {canShareCustomerBooking(currentUser) && (
                <>
                <Button size="sm" variant="outline-primary" disabled={!isNodeBookingAvailable(node)} title={!isNodeBookingAvailable(node) ? "This node is not accepting new bookings." : "Booking QR"} aria-label={`Booking QR for ${customer.CustomerNameTH || customer.CustomerNameEN || "customer"}`} onClick={() => onBookingQr(customer, nodeId)}>
                  <i className="fa fa-qrcode mr-1" aria-hidden="true" />Booking QR
                </Button>
                <Button size="sm" variant="outline-primary" disabled={!isNodeBookingAvailable(node) || getAvailabilityStatus(customer, node) !== "available"} title="CPAP QR" aria-label={`CPAP QR for ${customer.CustomerNameTH || customer.CustomerNameEN || "customer"}`} onClick={() => onBookingQr(customer, nodeId, "cpap")}>
                  <i className="fa fa-qrcode mr-1" aria-hidden="true" />CPAP QR
                </Button>
                </>
              )}
              {canManage && (
                <Button size="sm" variant="light" aria-label={`Edit customer ${customer.CustomerNameTH || customer.CustomerNameEN || ""}`} onClick={() => onEditCustomer(customer, nodeId)}>Edit</Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    ) : <p className="node-card__empty">No customers found.</p>}
  </div>
);

export default NodeList;
